import { Session } from 'next-auth';
import useSWR from 'swr';

type FetchOptions = {
  path?: {
    [key: string]: string;
  };
  query?: {
    [key: string]: unknown;
  };
  signal?: AbortSignal;
};

export interface UseAuthenticatedDCBEConfig {
  shouldFetch?: boolean;
  suspense?: boolean;
}

/**
 * A hook to fetch data from authenticated api's in DC BE. Only when the accessToken is set the call will be done.
 * this prevents multiple calls when de state is still loading
 * @param fetcher The DC BE client API fetch method
 * @param options The options for the API endpoint (include `signal` here when needed)
 * @param keys Keys on which the data needs to be fetched
 * @param sessionData the session object from DXP/Auth.
 * @param config SWR-related flags: `shouldFetch`, `suspense`
 * @returns useSWR response object
 *
 * @example
 * import { getApiLabelsByLabelPricingDynamic } from "@eneco/dc-be/client";
 * const {data, isLoading, error} = useAuthenticatedDCBE(getApiLabelsByLabelPricingDynamic, {
 *   query: {
 *     startDate: '2024-08-01',
 *     endDate: '2024-08-01',
 *     aggregation: 'hourly',
 *   }
 * })
 */
export default function useAuthenticatedDCBE<T extends FetchOptions, W extends { response: { status: number } }>(
  fetcher: (options: T) => Promise<W>,
  options: T,
  keys: string[] | null = [],
  sessionData: Session | null,
  config: UseAuthenticatedDCBEConfig = {},
) {
  const { shouldFetch = true, suspense } = config;
  let hasPath = true;
  if (options.path) {
    Object.values(options.path).forEach(value => {
      if (!value) {
        hasPath = false;
      }
    });
  }
  const computedShouldFetch = sessionData?.accessToken && keys && keys.length > 0 && hasPath && shouldFetch;
  const swrKey = computedShouldFetch ? keys.join() : null;

  const {
    data,
    error: swrError,
    isLoading,
  } = useSWR<W>(swrKey, () => (computedShouldFetch ? fetcher(options) : Promise.reject('error')), {
    suspense,
  });
  let error = swrError;

  const response = data?.response;
  if (response?.status && response.status > 400) {
    error = 'Something went wrong';
  }

  return { data, error, isLoading };
}
